import React, { useState, useEffect, useCallback } from "react";
import {
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Snackbar,
  Dialog,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import PropTypes from "prop-types";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import CustomModal from "shared/components/MDmodal/CustomModal";
import FormCommerce from "../Form/FormCommerce";
import {
  getAllCommerce,
  updateCommerceService,
  deleteCommerceService,
} from "modules/bitacora/services/Configuracion/Comercio/comercioService";

function TablaComercio({ refreshFlag }) {
  const [commerces, setCommerces] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [commerceSelect, setCommerceSelect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [commerceToDelete, setCommerceToDelete] = useState(null);

  const showNotification = useCallback((message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const fetchCommerce = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllCommerce();
      const parsedData = typeof data === "string" ? JSON.parse(data) : data;
      setCommerces(parsedData || []);
    } catch (err) {
      console.error("❌ Error al obtener comercios:", err);
      showNotification("Error al cargar comercios", "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchCommerce();
  }, [refreshFlag]);

  const handleEditar = useCallback((comercio) => {
    setCommerceSelect(comercio);
    setOpenModal(true);
  }, []);

  const handleEliminarClick = useCallback((id) => {
    setCommerceToDelete(id);
    setOpenDeleteDialog(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!commerceToDelete) return;
    try {
      setLoading(true);
      await deleteCommerceService(commerceToDelete);
      await fetchCommerce();
      showNotification("Comercio eliminado correctamente", "success");
    } catch (err) {
      console.error("Error al eliminar comercio:", err);
      const errorMessage = err.message?.includes("token")
        ? "Su sesión ha expirado. Por favor inicie sesión nuevamente."
        : "Error al eliminar comercio";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
      setCommerceToDelete(null);
    }
  }, [commerceToDelete, fetchCommerce, showNotification]);

  const handleActualizar = useCallback(
    async (datos) => {
      if (!commerceSelect) return;
      try {
        setLoading(true);
        await updateCommerceService(commerceSelect.id, {
          name: datos.name,
          localization: datos.localization,
          code: datos.code,
          status: datos.status,
        });
        await fetchCommerce();
        setOpenModal(false);
        showNotification("Comercio actualizado correctamente", "success");
      } catch (err) {
        console.error("Error al actualizar comercio:", err);
        const message = err.response?.data?.message || "Error al actualizar comercio";
        showNotification(message, "error");
      } finally {
        setLoading(false);
      }
    },
    [commerceSelect, fetchCommerce, showNotification]
  );

  const columns = [
    { Header: "Nombre", accessor: "name", width: "30%" },
    { Header: "Localización", accessor: "localization", width: "30%" },
    { Header: "Código", accessor: "code", width: "15%" },
    { Header: "Estado", accessor: "status", width: "15%" },
    { Header: "Acciones", accessor: "acciones", align: "center", width: "5%" },
  ];

  const rows = commerces.map((commerce) => ({
    name: (
      <MDTypography variant="button" fontWeight="medium">
        {commerce.name || "Sin nombre"}
      </MDTypography>
    ),
    localization: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {commerce.localization || "Sin localización"}
      </MDTypography>
    ),
    code: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {commerce.code || "Sin código"}
      </MDTypography>
    ),
    status: (
      <MDTypography
        variant="caption"
        color={commerce.status === true ? "success" : "error"}
        fontWeight="medium"
      >
        {commerce.status === true ? "Activo" : "Inactivo"}
      </MDTypography>
    ),
    acciones: (
      <MDBox display="flex" justifyContent="center">
        <Tooltip title="Editar Comercio">
          <IconButton color="info" onClick={() => handleEditar(commerce)}>
            <Edit />
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar comercio">
          <IconButton color="error" onClick={() => handleEliminarClick(commerce.id)}>
            <Delete />
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <>
      <MDBox
        mx={-1}
        mt={-1}
        py={2}
        px={2}
        variant="gradient"
        bgColor="success"
        borderRadius="lg"
        coloredShadow="info"
      >
        <MDTypography variant="h6" color="white">
          Gestión de Comercios
        </MDTypography>
      </MDBox>

      <MDBox pt={1} px={-2}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress color="info" />
          </MDBox>
        ) : error ? (
          <MDBox p={2}>
            <Alert severity="error">{error}</Alert>
          </MDBox>
        ) : (
          <DataTable
            table={{ columns, rows }}
            isSorted
            entriesPerPage={false}
            showTotalEntries
            noEndBorder
          />
        )}
      </MDBox>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDeleteDialog}
        onClose={() => !loading && setOpenDeleteDialog(false)}
        PaperProps={{
          sx: { minWidth: "400px", borderRadius: "12px", padding: "16px" },
        }}
      >
        <MDBox display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: "error.light", mr: 2 }}>
            <Delete color="error" />
          </Avatar>
          <MDTypography variant="h6" fontWeight="medium">
            Confirmar eliminación
          </MDTypography>
        </MDBox>

        <DialogContent>
          <MDTypography variant="body2">
            Esta acción eliminará el comercio del sistema.
          </MDTypography>
          <MDTypography variant="body2" fontWeight="bold" mt={1}>
            ¿Deseas continuar?
          </MDTypography>
        </DialogContent>

        <MDBox display="flex" justifyContent="space-between" px={3} pb={2}>
          <MDButton
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            color="error"
            disabled={loading}
            sx={{ minWidth: "120px" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            onClick={confirmDelete}
            color="error"
            variant="gradient"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: "120px" }}
          >
            {loading ? "Eliminando..." : "Confirmar"}
          </MDButton>
        </MDBox>
      </Dialog>

      <CustomModal
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          setError(null);
        }}
        title={`Editar Comercio - ${commerceSelect?.name || ""}`}
      >
        {commerceSelect && (
          <FormCommerce
            initialData={{
              name: commerceSelect?.name || "",
              localization: commerceSelect?.localization || "",
              code: commerceSelect?.code ?? "",
            }}
            onSubmit={handleActualizar}
            isEditMode
            loading={loading}
          />
        )}
      </CustomModal>
    </>
  );
}

TablaComercio.propTypes = {
  refreshFlag: PropTypes.bool,
};

export default TablaComercio;
